// Attaches customizer thumbnails to cart lines once their upload is live.
//
// The Cart Transform function swaps the line image from the `_newImageUrl`
// property. Shopify rejects the whole update (`image_not_found`, which also
// drops the line's tier price) when the file isn't in the shop, so the function
// only uses the image once the line also carries `_imageReady`. This module
// sets that flag after it has seen the image load.
//
// The Vue customizer adds to cart before its preview image has finished
// uploading, under `_pendingImageUrl` so the drawer doesn't show a broken
// image; confirming renames it to `_newImageUrl`. Photo labels wait for their
// upload and add `_newImageUrl` directly, so those lines only need the flag.
//
// The cart itself is the queue: every pending line carries its own URL, so
// lines are handled independently and survive navigation. localStorage only
// remembers that there is something to check on the next page load, plus a
// copy of each optimistic upload so a later page view can re-send it once if
// it never arrived.
//
// When an upload fails, the line also gets `_uploadError` saying why (HTTP
// status, or the browser's network error and how long it took), plus how the
// re-send ended, so a lost image on an order shows its cause. It is only a
// note: the Cart Transform never reads it, and the drawer hides `_` keys.
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const PENDING_PROPERTY = '_pendingImageUrl';
const IMAGE_PROPERTY = '_newImageUrl';
const READY_PROPERTY = '_imageReady';
const ERROR_PROPERTY = '_uploadError';
const STORAGE_KEY = 'ib-pending-cart-images';
const COPIES_KEY = 'ib-pending-cart-image-copies';
const OUTCOMES_KEY = 'ib-pending-cart-image-outcomes';
// Checks for the uploaded image per page view, and the wait (ms) between them.
const PROBE_ATTEMPTS = 15;
const PROBE_INTERVAL = 1000;
// Page views to keep checking before giving up on an image that never arrived.
const MAX_ROUNDS = 3;
// Longest `_uploadError` written to a line.
const MAX_ERROR_LENGTH = 250;

/**
 * @typedef {object} UploadCopy The upload request the customizer sent
 * @property {string} endpoint
 * @property {string} filename
 * @property {string} image Data URL
 */

/**
 * @typedef {object} UploadOutcome How a line's image uploads ended
 * @property {string} [upload] 'ok', or why the customizer's upload failed
 * @property {string} [resend] How the one re-send ended
 */

/** @type {Set<string>} URLs being checked on this page */
const inFlight = new Set();
/** Serializes cart writes so two lines never change at the same time. */
let writeQueue = Promise.resolve();

/** @returns {Record<string, number>} Pending URL → page views spent checking it */
function readRounds() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

/** @param {Record<string, number>} rounds */
function writeRounds(rounds) {
  try {
    if (Object.keys(rounds).length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rounds));
  } catch {
    // Storage unavailable — the current page still attaches its own images.
  }
}

/** @returns {Record<string, UploadCopy>} Pending URL → its upload request */
function readCopies() {
  try {
    return JSON.parse(localStorage.getItem(COPIES_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

/** @param {Record<string, UploadCopy>} copies */
function writeCopies(copies) {
  try {
    if (Object.keys(copies).length === 0) {
      localStorage.removeItem(COPIES_KEY);
      return;
    }
    localStorage.setItem(COPIES_KEY, JSON.stringify(copies));
  } catch {
    // Storage full or unavailable — the image just won't be re-sent.
  }
}

/** @param {string} url */
function forgetCopy(url) {
  const copies = readCopies();
  if (!(url in copies)) return;
  delete copies[url];
  writeCopies(copies);
}

/** @returns {Record<string, UploadOutcome>} Pending URL → how its uploads ended */
function readOutcomes() {
  try {
    return JSON.parse(localStorage.getItem(OUTCOMES_KEY) ?? '{}') ?? {};
  } catch {
    return {};
  }
}

/** @param {Record<string, UploadOutcome>} outcomes */
function writeOutcomes(outcomes) {
  try {
    if (Object.keys(outcomes).length === 0) {
      localStorage.removeItem(OUTCOMES_KEY);
      return;
    }
    localStorage.setItem(OUTCOMES_KEY, JSON.stringify(outcomes));
  } catch {
    // Storage unavailable — this page still notes its own failures.
  }
}

/**
 * @param {string} url
 * @param {UploadOutcome} outcome Merged into what is already recorded
 */
function saveOutcome(url, outcome) {
  const outcomes = readOutcomes();
  outcomes[url] = { ...outcomes[url], ...outcome };
  writeOutcomes(outcomes);
}

/** @param {string} url */
function forgetOutcome(url) {
  const outcomes = readOutcomes();
  if (!(url in outcomes)) return;
  delete outcomes[url];
  writeOutcomes(outcomes);
}

/**
 * @param {UploadOutcome} [outcome]
 * @returns {string | undefined} The line's `_uploadError`, when something failed
 */
function errorText(outcome) {
  const upload = outcome?.upload;
  if (outcome?.resend) {
    // A re-send only runs when the image never showed up.
    const first = upload === 'ok' ? 'upload ok, image not served' : (upload ?? 'no upload result');
    return `${first}; resend ${outcome.resend}`.slice(0, MAX_ERROR_LENGTH);
  }
  if (!upload || upload === 'ok') return undefined;
  return upload.slice(0, MAX_ERROR_LENGTH);
}

/**
 * @param {Promise<Response>} request An upload request
 * @param {number} startedAt When it was sent (ms)
 * @returns {Promise<string>} 'ok' once the upload function accepted it, otherwise why it failed
 */
async function uploadOutcome(request, startedAt) {
  let reason;
  try {
    const response = await request;
    if (response.ok) return 'ok';
    // 502 = a Shopify step failed inside the function, 400 = bad input
    reason = `HTTP ${response.status}`;
  } catch (error) {
    // The request never got an answer: blocked, offline, or the connection dropped
    reason = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    if (!navigator.onLine) reason += ', offline';
  }
  return `${reason} after ${((Date.now() - startedAt) / 1000).toFixed(1)}s`;
}

/**
 * Sends a lost upload again from the copy kept at add time, and records how it
 * ended. The copy is dropped first, so an image is never re-sent more than once.
 * @param {string} url
 * @returns {Promise<boolean>} Whether the image is live afterwards
 */
async function resendUpload(url) {
  const copy = readCopies()[url];
  if (!copy) {
    // Later rounds find the copy already used; only note one that never existed.
    if (!readOutcomes()[url]?.resend) saveOutcome(url, { resend: 'not sent, no copy' });
    return false;
  }
  forgetCopy(url);

  const startedAt = Date.now();
  const request = fetch(copy.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: copy.image, filename: copy.filename }),
  });
  const resend = await uploadOutcome(request, startedAt);
  const live = resend === 'ok' && (await waitForImage(url));
  saveOutcome(url, { resend: resend === 'ok' && !live ? 'ok, image not served' : resend });
  return live;
}

/**
 * @param {string} url
 * @returns {Promise<boolean>} Whether the image is being served
 */
function imageLoads(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    // Cache-bust so an earlier 404 for the same URL isn't replayed.
    image.src = `${url}?v=${Date.now()}`;
  });
}

/**
 * @param {string} url
 * @param {number} [attempts]
 * @returns {Promise<boolean>} Whether the image became available
 */
async function waitForImage(url, attempts = PROBE_ATTEMPTS) {
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, PROBE_INTERVAL));
    if (await imageLoads(url)) return true;
  }
  return false;
}

/**
 * @param {any} item A cart line
 * @returns {string | undefined} The line's image URL while it still needs confirming
 */
function imageToConfirm(item) {
  const properties = item.properties ?? {};
  if (properties[PENDING_PROPERTY]) return properties[PENDING_PROPERTY];
  return properties[IMAGE_PROPERTY] && !properties[READY_PROPERTY] ? properties[IMAGE_PROPERTY] : undefined;
}

/** @returns {Promise<any>} The current cart */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * @param {string} url
 * @returns {Promise<any>} The cart line still waiting on this image, if any
 */
async function findLine(url) {
  const cart = await fetchCart();
  return cart.items?.find((/** @type {any} */ line) => imageToConfirm(line) === url);
}

/**
 * Marks a single cart line's image as live (moving a pending URL to the real
 * property).
 * @param {string} url
 */
async function confirmLine(url) {
  const item = await findLine(url);
  if (!item) return;

  const properties = { ...item.properties, [IMAGE_PROPERTY]: url, [READY_PROPERTY]: '1' };
  delete properties[PENDING_PROPERTY];
  const error = errorText(readOutcomes()[url]);
  if (error) properties[ERROR_PROPERTY] = error;
  await changeLine(item, properties);
}

/**
 * Notes on a single cart line why its image upload failed. Never marks the
 * image ready.
 * @param {string} url
 */
async function noteUploadError(url) {
  const error = errorText(readOutcomes()[url]);
  if (!error) return;
  const item = await findLine(url);
  if (!item || item.properties?.[ERROR_PROPERTY] === error) return;

  await changeLine(item, { ...item.properties, [ERROR_PROPERTY]: error });
}

/**
 * Replaces a cart line's properties and re-renders the cart sections (the line
 * key changes with its properties).
 * @param {any} item The line, read fresh: /cart/change.js resets the quantity
 *   to 1 unless it is sent along, and the shopper may have changed it while
 *   the image uploaded
 * @param {Record<string, string>} properties
 */
async function changeLine(item, properties) {
  const components = Array.from(document.querySelectorAll('cart-items-component'));
  const sectionIds = components.map((component) => component.dataset.sectionId).filter(Boolean);

  const body = JSON.stringify({
    id: item.key,
    quantity: item.quantity,
    properties,
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  if (!response.ok) return;

  const updatedCart = await response.json();
  for (const component of components) {
    const sectionId = component.dataset.sectionId;
    const html = sectionId ? updatedCart.sections?.[sectionId] : null;
    if (!html) continue;
    await morphSection(sectionId, html, { mode: component.dataset.drawer !== undefined ? 'hydration' : 'full' });
  }
}

/**
 * @param {string} url
 * @param {number} round Page views spent on this image, counting this one
 */
async function attachLine(url, round) {
  try {
    // An upload that failed before its line reached the cart, or on a page
    // that closed before noting it.
    await queueWrite(() => noteUploadError(url));

    // A later page view checks once, then re-sends a lost upload once.
    const laterVisit = round > 1;
    let live = await waitForImage(url, laterVisit ? 1 : PROBE_ATTEMPTS);
    if (!live && laterVisit) {
      live = await resendUpload(url);
      if (!live) await queueWrite(() => noteUploadError(url));
    }
    if (!live) return;

    await queueWrite(() => confirmLine(url));

    const rounds = readRounds();
    delete rounds[url];
    writeRounds(rounds);
    forgetCopy(url);
    forgetOutcome(url);
  } finally {
    inFlight.delete(url);
  }
}

/**
 * Runs a cart write after the ones already queued.
 * @param {() => Promise<void>} write
 */
function queueWrite(write) {
  writeQueue = writeQueue.then(write).catch(() => {});
  return writeQueue;
}

/**
 * Remembers an image that is about to be added to the cart, so a later page
 * view picks it up even if the shopper navigates away straight after adding.
 * @param {string} url
 * @param {UploadCopy} [upload] The upload request, kept to re-send it once
 */
window.trackPendingCartImage = function (url, upload) {
  const rounds = readRounds();
  rounds[url] ??= 0;
  writeRounds(rounds);

  if (!upload) return;
  const copies = readCopies();
  copies[url] = upload;
  writeCopies(copies);
};

/**
 * Records how a line's background upload ends. A failure is noted on the line
 * straight away, so it is there even if the shopper checks out next.
 * @param {string} url The line's pending image URL
 * @param {Promise<Response>} request The upload request
 * @param {number} startedAt When it was sent (ms)
 */
window.trackCartImageUpload = async function (url, request, startedAt) {
  const upload = await uploadOutcome(request, startedAt);
  saveOutcome(url, { upload });
  // Before the add finishes the line isn't in the cart yet; attachLine notes it then.
  if (upload !== 'ok') await queueWrite(() => noteUploadError(url));
};

/**
 * Finds cart lines whose image isn't confirmed yet and confirms each one as
 * soon as its image is live. Safe to call repeatedly.
 */
window.attachPendingCartImages = async function () {
  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return;
  }

  const stored = readRounds();
  /** @type {Record<string, number>} */
  const rounds = {};

  for (const item of cart.items ?? []) {
    const url = imageToConfirm(item);
    if (!url) continue;

    rounds[url] = stored[url] ?? 0;
    if (inFlight.has(url) || rounds[url] >= MAX_ROUNDS) continue;

    rounds[url] += 1;
    inFlight.add(url);
    attachLine(url, rounds[url]);
  }

  // Rewriting from the cart drops URLs whose line is gone.
  writeRounds(rounds);
  const copies = readCopies();
  for (const url of Object.keys(copies)) {
    if (!(url in rounds)) delete copies[url];
  }
  writeCopies(copies);
  const outcomes = readOutcomes();
  for (const url of Object.keys(outcomes)) {
    if (!(url in rounds)) delete outcomes[url];
  }
  writeOutcomes(outcomes);
};

// theme.liquid marks pages whose cart still has images to confirm, which also
// covers lines added on another device or before the flag existed.
const cartHasImagesToConfirm = document.querySelector('script[data-confirm-cart-images]') !== null;

if (cartHasImagesToConfirm || Object.values(readRounds()).some((count) => count < MAX_ROUNDS)) {
  window.attachPendingCartImages();
}
